import { type ColumnDef } from '@tanstack/react-table';

import { SeloCaptura } from '@/lib/captura/situacao-captura';
import { ROTULO_TIPO_EXECUCAO } from '@/lib/captura/textos';
import { VISUAL_EXECUCAO } from '@/lib/captura/visual';
import { cx } from '@/lib/cx';
import { formatarNumero } from '@/lib/formato';
import { formatarDiaHoraNoFuso } from '@/lib/fuso';
import { type MetaColuna } from '@/ui/components/tabela-dados';
import { type ContadoresCaptura, type ExecucaoCaptura } from '@fiscoai/contracts';

const meta = (m: MetaColuna) => m;

/** Contador alinhado à direita; zero fica apagado para o olho achar o que veio. */
function contador(
  id: keyof ContadoresCaptura,
  titulo: string,
  tom?: 'perigo',
): ColumnDef<ExecucaoCaptura> {
  return {
    id,
    header: titulo,
    meta: meta({ alinhar: 'direita', largura: '6rem' }),
    cell: ({ row }) => {
      const n = row.original.contadores[id];
      return (
        <span
          className={cx(
            'tabular',
            n === 0
              ? 'text-texto-fraco'
              : tom === 'perigo'
                ? 'font-medium text-perigo'
                : 'font-medium',
          )}
        >
          {formatarNumero(n)}
        </span>
      );
    },
  };
}

/** Colunas do histórico; as horas no fuso da instalação. Só "Quando" ordena (pelo início). */
export function colunasExecucoes(fuso: string): ColumnDef<ExecucaoCaptura>[] {
  return [
    {
      id: 'quando',
      header: 'Quando',
      meta: meta({ largura: '8rem', ordenavel: 'iniciadaEm' }),
      cell: ({ row }) => (
        <time
          dateTime={row.original.iniciadaEm}
          className="tabular whitespace-nowrap text-texto-suave"
        >
          {formatarDiaHoraNoFuso(row.original.iniciadaEm, fuso)}
        </time>
      ),
    },
    {
      id: 'tipo',
      header: 'Tipo',
      meta: meta({ largura: '7rem' }),
      cell: ({ row }) => ROTULO_TIPO_EXECUCAO[row.original.tipo],
    },
    {
      id: 'resultado',
      header: 'Resultado',
      meta: meta({ largura: '16rem' }),
      cell: ({ row }) => {
        const e = row.original;
        return (
          <span className="grid min-w-0 gap-0.5">
            <span className="inline-flex items-center gap-1.5">
              <SeloCaptura visual={VISUAL_EXECUCAO[e.status]} />
              {e.cstat && (
                <span
                  className="tabular text-xs text-texto-fraco"
                  title="Código de status da SEFAZ (cStat)"
                >
                  {e.cstat}
                </span>
              )}
            </span>
            {e.motivo && e.status !== 'CONCLUIDA' && (
              <span className="truncate text-xs text-texto-fraco" title={e.motivo}>
                {e.motivo}
              </span>
            )}
          </span>
        );
      },
    },
    contador('importadas', 'Notas'),
    contador('resumos', 'Resumos'),
    contador('eventos', 'Eventos'),
    contador('ciencias', 'Ciências'),
    contador('erros', 'Erros', 'perigo'),
    {
      id: 'quem',
      header: 'Quem',
      meta: meta({ largura: '10rem' }),
      cell: ({ row }) =>
        row.original.disparadaPor ? (
          <span className="truncate">{row.original.disparadaPor.nome}</span>
        ) : (
          <span className="text-texto-fraco">Agenda</span>
        ),
    },
  ];
}
